import React, { useRef, useEffect, useState, useMemo } from 'react';
import { format } from 'date-fns';
import { VariableSizeList as List } from 'react-window';
import { Chat, Message } from '../types';
import { MessageBubble } from './MessageBubble';
import { Search, X, ChevronDown, ChevronUp, Calendar, ChevronsUp, ChevronsDown } from 'lucide-react';
import './ChatView.css';

interface ChatViewProps {
  chat: Chat;
  searchQuery?: string;
}

export const ChatView: React.FC<ChatViewProps> = ({ chat }) => {
  const [inChatSearch, setInChatSearch] = useState('');
  const [searchResults, setSearchResults] = useState<Message[]>([]);
  const [currentResultIndex, setCurrentResultIndex] = useState(0);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<List>(null);
  const [containerHeight, setContainerHeight] = useState(600);

  // Measure container height for virtualization
  useEffect(() => {
    const updateHeight = () => {
      if (messagesContainerRef.current) {
        const rect = messagesContainerRef.current.getBoundingClientRect();
        setContainerHeight(rect.height);
      }
    };
    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, []);

  // Close date picker when clicking outside
  useEffect(() => {
    if (!showDatePicker) return;
    
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.date-jump-container')) {
        setShowDatePicker(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDatePicker]);

  // Scroll to bottom on chat change
  useEffect(() => {
    if (listRef.current) {
      setTimeout(() => {
        listRef.current?.scrollToItem(flattenedItems.length - 1, 'end');
      }, 100);
    }
  }, [chat.id]);

  // Group messages by date (memoized for performance)
  const groupedMessages = useMemo(() => {
    const groups: Array<{ date: string; messages: Message[] }> = [];
    let currentDate = '';
    let currentGroup: Message[] = [];

    chat.messages.forEach((message) => {
      if (!message.unixMs) {
        currentGroup.push(message);
        return;
      }

      const messageDate = format(new Date(message.unixMs), 'yyyy-MM-dd');
      
      if (messageDate !== currentDate) {
        if (currentGroup.length > 0) {
          groups.push({ date: currentDate, messages: currentGroup });
        }
        currentDate = messageDate;
        currentGroup = [message];
      } else {
        currentGroup.push(message);
      }
    });

    if (currentGroup.length > 0) {
      groups.push({ date: currentDate, messages: currentGroup });
    }

    return groups;
  }, [chat.messages]);

  // Flatten messages with date separators (memoized for performance)
  const flattenedItems = useMemo(() => {
    const items: Array<{ type: 'date' | 'message'; data: any; index: number }> = [];
    
    groupedMessages.forEach((group) => {
      items.push({ type: 'date', data: group.date, index: items.length });
      group.messages.forEach((message) => {
        items.push({ type: 'message', data: message, index: items.length });
      });
    });

    return items;
  }, [groupedMessages]);

  // Search within chat (debounced for performance)
  useEffect(() => {
    if (!inChatSearch.trim()) {
      setSearchResults([]);
      setCurrentResultIndex(0);
      return;
    }

    // Debounce search to avoid lag while typing
    const timeoutId = setTimeout(() => {
      const query = inChatSearch.toLowerCase();
      const results = chat.messages.filter(
        (msg) =>
          msg.text.toLowerCase().includes(query) ||
          msg.sender?.toLowerCase().includes(query)
      );
      
      setSearchResults(results);
      setCurrentResultIndex(0);
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [inChatSearch, chat.messages]);

  const scrollToMessage = (messageId: string) => {
    const index = flattenedItems.findIndex(item => 
      item.type === 'message' && item.data.id === messageId
    );
    if (index !== -1 && listRef.current) {
      listRef.current.scrollToItem(index, 'center');
    }
  };

  const goToNextResult = () => {
    if (searchResults.length === 0) return;
    const newIndex = (currentResultIndex + 1) % searchResults.length;
    setCurrentResultIndex(newIndex);
    scrollToMessage(searchResults[newIndex].id);
  };

  const goToPrevResult = () => {
    if (searchResults.length === 0) return;
    const newIndex =
      currentResultIndex === 0 ? searchResults.length - 1 : currentResultIndex - 1;
    setCurrentResultIndex(newIndex);
    scrollToMessage(searchResults[newIndex].id);
  };

  const goToFirstMessage = () => {
    if (listRef.current) {
      listRef.current.scrollToItem(0, 'start');
    }
  };

  const goToLastMessage = () => {
    if (listRef.current) {
      listRef.current.scrollToItem(flattenedItems.length - 1, 'end');
    }
  };

  const jumpToDate = (dateStr: string) => {
    if (!dateStr) return;
    
    const targetDate = new Date(dateStr);
    targetDate.setHours(0, 0, 0, 0);
    
    // Find the first item (date separator or message) on or after the target date
    const index = flattenedItems.findIndex(item => {
      if (item.type === 'date') {
        const itemDate = new Date(item.data);
        itemDate.setHours(0, 0, 0, 0);
        return itemDate >= targetDate;
      }
      if (item.type === 'message' && item.data.unixMs) {
        const itemDate = new Date(item.data.unixMs);
        itemDate.setHours(0, 0, 0, 0);
        return itemDate >= targetDate;
      }
      return false;
    });
    
    if (index !== -1 && listRef.current) {
      listRef.current.scrollToItem(index, 'start');
      setShowDatePicker(false);
    }
  };

  // Get min and max dates from chat messages
  const dateRange = useMemo(() => {
    if (chat.messages.length === 0) return { min: '', max: '' };
    
    const validMessages = chat.messages.filter(m => m.unixMs);
    if (validMessages.length === 0) return { min: '', max: '' };
    
    const timestamps = validMessages.map(m => m.unixMs!);
    const minDate = new Date(Math.min(...timestamps));
    const maxDate = new Date(Math.max(...timestamps));
    
    return {
      min: format(minDate, 'yyyy-MM-dd'),
      max: format(maxDate, 'yyyy-MM-dd')
    };
  }, [chat.messages]);

  // Get item size for virtualization (tablet-optimized)
  const getItemSize = (index: number) => {
    const item = flattenedItems[index];
    if (item.type === 'date') return 48;
    const message = item.data as Message;
    const lines = message.text.split('\n').length;
    const baseHeight = 52;
    return Math.min(baseHeight + (lines * 20), 300);
  };

  // Render individual row in virtual list
  const Row = ({ index, style }: { index: number; style: React.CSSProperties }) => {
    const item = flattenedItems[index];
    
    if (item.type === 'date') {
      const dateStr = item.data
        ? format(new Date(item.data), 'MMMM d, yyyy')
        : 'Unknown Date';
      
      return (
        <div style={style}>
          <div className="date-separator-wrapper">
            <div className="date-separator">
              <Calendar size={14} />
              <span>{dateStr}</span>
            </div>
          </div>
        </div>
      );
    }

    const message = item.data as Message;
    const prevItem = index > 0 ? flattenedItems[index - 1] : null;
    const showSender =
      prevItem?.type === 'date' ||
      (prevItem?.type === 'message' && prevItem.data.sender !== message.sender);

    const highlight =
      inChatSearch && searchResults.some((r) => r.id === message.id)
        ? inChatSearch
        : undefined;

    return (
      <div style={style} id={`msg-${message.id}`} className="message-row">
        <MessageBubble
          message={message}
          showSender={showSender}
          highlightText={highlight}
        />
      </div>
    );
  };

  return (
    <div className="chat-view">
      <div className="chat-header">
        <div className="chat-header-info">
          <h2>{chat.title}</h2>
          <p>
            {chat.messageCount.toLocaleString()} messages • {chat.participants.length} participants
          </p>
        </div>

        <div className="chat-search">
          <button
            className="icon-button"
            onClick={goToFirstMessage}
            title="Jump to first message"
          >
            <ChevronsUp size={18} />
          </button>
          <button
            className="icon-button"
            onClick={goToLastMessage}
            title="Jump to last message"
          >
            <ChevronsDown size={18} />
          </button>
          
          <div className="date-jump-container">
            <button
              className={`icon-button ${showDatePicker ? 'active' : ''}`}
              onClick={() => setShowDatePicker(!showDatePicker)}
              title="Jump to date"
            >
              <Calendar size={18} />
            </button>
            {showDatePicker && (
              <div className="date-picker-popup">
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  min={dateRange.min}
                  max={dateRange.max}
                  className="date-picker-input"
                />
                <button
                  className="jump-button"
                  onClick={() => jumpToDate(selectedDate)}
                  disabled={!selectedDate}
                >
                  Go
                </button>
              </div>
            )}
          </div>
          
          <div className="search-input-container">
            <Search size={16} />
            <input
              type="text"
              placeholder="Search in chat..."
              value={inChatSearch}
              onChange={(e) => setInChatSearch(e.target.value)}
            />
            {inChatSearch && (
              <button
                className="clear-search"
                onClick={() => setInChatSearch('')}
                title="Clear search"
              >
                <X size={16} />
              </button>
            )}
          </div>

          {searchResults.length > 0 && (
            <div className="search-results-nav">
              <span>
                {currentResultIndex + 1} of {searchResults.length}
              </span>
              <div className="nav-buttons">
                <button onClick={goToPrevResult} title="Previous result">
                  <ChevronUp size={16} />
                </button>
                <button onClick={goToNextResult} title="Next result">
                  <ChevronDown size={16} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="messages-container" ref={messagesContainerRef}>
        <List
          ref={listRef}
          height={containerHeight}
          itemCount={flattenedItems.length}
          itemSize={getItemSize}
          width="100%"
          className="messages-list"
          initialScrollOffset={999999}
        >
          {Row}
        </List>
      </div>
    </div>
  );
};
